/**
 * TanStack Query хуки воркспейсов, участников и доступов + хук активного воркспейса.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {useAtom} from 'jotai';
import {useEffect, useMemo} from 'react';
import {useAuth} from '@api/auth';
import {IS_MOCK} from '@api/mock-mode';
import {workspacesClient} from './client';
import type {SetPasswordInput, SetPasswordResult} from '@api/users';
import {activeWorkspaceIdAtom} from './atoms';
import {readModules, type WorkspaceModules} from './lib/modules';
import type {
  AddWorkspaceMemberInput,
  CreateWorkspaceInput,
  GrantActionInput,
  UpdateWorkspaceInput,
  UpdateWorkspaceMemberInput,
  WorkspaceSummary,
} from './types';

export const workspaceKeys = {
  myWorkspaces: () => ['workspaces', 'mine'] as const,
  allWorkspaces: () => ['workspaces', 'all'] as const,
  members: (workspaceSid: string) =>
    ['workspaces', workspaceSid, 'members'] as const,
  branches: (workspaceSid: string) =>
    ['workspaces', workspaceSid, 'branches'] as const,
  grants: (workspaceSid: string) =>
    ['workspaces', workspaceSid, 'grants'] as const,
  myAccess: (workspaceSid: string) =>
    ['workspaces', workspaceSid, 'access', 'my'] as const,
  actions: () => ['workspaces', 'actions'] as const,
  roles: () => ['workspaces', 'roles'] as const,
};

const invalidateDashboardScope = (
  queryClient: QueryClient,
  workspaceSid: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({queryKey: ['dashboard', workspaceSid]}),
    queryClient.invalidateQueries({queryKey: ['workspace-rating']}),
  ]);

/** Список воркспейсов текущего пользователя. */
export const useMyWorkspaces = () => {
  const {currentUser} = useAuth();
  return useQuery({
    queryKey: [...workspaceKeys.myWorkspaces(), currentUser?.sid],
    queryFn: () => workspacesClient.listMyWorkspaces(),
    enabled: !IS_MOCK && Boolean(currentUser?.sid),
    staleTime: 60_000,
  });
};

/**
 * Активный воркспейс + средства переключения.
 *
 * Самовосстановление: если активный id пуст или указывает на недоступный воркспейс,
 * выбираем первый доступный (пользователь с единственным воркспейсом работает
 * «из коробки», без явного выбора).
 */
export const useActiveWorkspace = () => {
  const {data: workspaces = [], isLoading} = useMyWorkspaces();
  const [activeId, setActiveId] = useAtom(activeWorkspaceIdAtom);
  const queryClient = useQueryClient();

  const active = useMemo<WorkspaceSummary | undefined>(
    () => workspaces.find((w) => w.sid === activeId),
    [workspaces, activeId],
  );

  // Нормализация выбора: пустой / устаревший id → первый доступный воркспейс.
  useEffect(() => {
    if (workspaces.length === 0) return;
    if (!activeId || !workspaces.some((w) => w.sid === activeId)) {
      setActiveId(workspaces[0].sid);
    }
  }, [workspaces, activeId, setActiveId]);

  /** Переключиться на воркспейс: сохранить выбор и перезагрузить scoped-данные. */
  const switchWorkspace = (sid: string) => {
    if (sid === activeId) return;
    setActiveId(sid);
    // Все workspace-scoped данные (участники/доступы/коммуникации) — рефетчим.
    queryClient.invalidateQueries();
  };

  return {
    workspaces,
    isLoading,
    active: active ?? workspaces[0],
    activeId: active?.sid ?? workspaces[0]?.sid ?? null,
    canSwitch: workspaces.length > 1,
    switchWorkspace,
  };
};

/** Полный воркспейс по идентификатору (участник или оператор). */
export const useWorkspace = (workspaceSid: string | null | undefined) =>
  useQuery({
    queryKey: ['workspaces', workspaceSid ?? '∅', 'full'] as const,
    queryFn: () => workspacesClient.getWorkspace(workspaceSid as string),
    enabled: Boolean(workspaceSid),
  });

/**
 * Включённые модули активного воркспейса + признаки «ещё грузится» / «не
 * прочитались».
 *
 * Дефолт — модули выключены, поэтому до загрузки воркспейса флаги ничего не
 * решают: тем, кто по ним редиректит (см. `ModuleGuard`), нужен `isPending`,
 * чтобы не выбросить пользователя с включённого раздела раньше времени.
 *
 * `isError` отделяет «модуль выключен» от «воркспейс не прочитался»: без него
 * упавший запрос выглядел бы как осознанно выключенный модуль, и раздел молча
 * показал бы заглушку «ещё не подключён» тому, у кого модуль включён.
 */
export const useWorkspaceModulesState = (): {
  modules: WorkspaceModules;
  isPending: boolean;
  /** Воркспейс не прочитался и в кэше ничего нет — флаги модулей неизвестны. */
  isError: boolean;
  /** Повторить запрос воркспейса (для кнопки «Повторить»). */
  retry: () => void;
} => {
  const {activeId, isLoading: workspacesLoading} = useActiveWorkspace();
  const {data: workspace, isLoading, isError, refetch} = useWorkspace(activeId);
  return useMemo(
    () => ({
      modules: readModules(workspace),
      // Нет активного воркспейса — ждать нечего (запрос выключен).
      isPending: workspacesLoading || (Boolean(activeId) && isLoading),
      // Фоновая ошибка поверх живого кэша не в счёт — данные для флагов есть.
      isError: isError && workspace === undefined,
      retry: () => void refetch(),
    }),
    [workspace, workspacesLoading, activeId, isLoading, isError, refetch],
  );
};

/** Включённые модули активного воркспейса (см. `lib/modules.ts`). */
export const useWorkspaceModules = (): WorkspaceModules =>
  useWorkspaceModulesState().modules;

// ── участники ────────────────────────────────────────────────────────────────

/** Филиалы видны администратору Workspace. */
export const useWorkspaceBranches = (
  workspaceSid: string | null | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: workspaceKeys.branches(workspaceSid ?? '∅'),
    queryFn: () => workspacesClient.listBranches(workspaceSid as string),
    enabled: Boolean(workspaceSid) && enabled,
  });

export const useCreateBranch = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      workspacesClient.createBranch(workspaceSid, name),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKeys.branches(workspaceSid),
        }),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

export const useUpdateBranch = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({branchSid, name}: {branchSid: string; name: string}) =>
      workspacesClient.updateBranch(workspaceSid, branchSid, name),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

export const useDeleteBranch = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (branchSid: string) =>
      workspacesClient.deleteBranch(workspaceSid, branchSid),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

/** Список участников воркспейса. */
export const useWorkspaceMembers = (
  workspaceSid: string | null | undefined,
) => {
  const {currentUser} = useAuth();
  return useQuery({
    queryKey: [...workspaceKeys.members(workspaceSid ?? '∅'), currentUser?.sid],
    queryFn: () => workspacesClient.listMembers(workspaceSid as string),
    enabled: Boolean(workspaceSid && currentUser?.sid),
  });
};

/** Добавить участника в воркспейс. */
export const useAddMember = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddWorkspaceMemberInput) =>
      workspacesClient.addMember(workspaceSid, input),
    // Роль участника материализует гранты — обновляем и members, и grants/access.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

/** Изменить участника. */
export const useUpdateMember = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      membershipSid,
      input,
    }: {
      membershipSid: string;
      input: UpdateWorkspaceMemberInput;
    }) => workspacesClient.updateMember(workspaceSid, membershipSid, input),
    // Смена роли пересинхронизирует гранты — обновляем весь scope воркспейса.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

/** Удалить участника. */
export const useRemoveMember = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipSid: string) =>
      workspacesClient.removeMember(workspaceSid, membershipSid),
    // Удаление участника отзывает его role-managed гранты — обновляем scope.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

/** Выдать/сменить пароль участника (manage-members или оператор). */
export const useSetMemberPassword = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      membershipSid,
      input,
    }: {
      membershipSid: string;
      input: SetPasswordInput;
    }): Promise<SetPasswordResult> =>
      workspacesClient.setMemberPassword(workspaceSid, membershipSid, input),
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]}),
  });
};

// ── доступы ──────────────────────────────────────────────────────────────────

/** Сводка доступов текущего пользователя в воркспейсе (гейтинг UI). */
export const useMyAccess = (workspaceSid: string | null | undefined) => {
  const {currentUser} = useAuth();
  return useQuery({
    queryKey: [
      ...workspaceKeys.myAccess(workspaceSid ?? '∅'),
      currentUser?.sid,
    ],
    queryFn: () => workspacesClient.myAccess(workspaceSid as string),
    enabled: !IS_MOCK && Boolean(workspaceSid && currentUser?.sid),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
};

/** Реестр ролей-пресетов (для форм управления участниками). */
export const useRoles = () =>
  useQuery({
    queryKey: workspaceKeys.roles(),
    queryFn: () => workspacesClient.listRoles(),
    staleTime: 5 * 60_000,
  });

/** Реестр действий (для форм выдачи доступов). */
export const useActions = () =>
  useQuery({
    queryKey: workspaceKeys.actions(),
    queryFn: () => workspacesClient.listActions(),
    staleTime: 10 * 60_000,
  });

/** Гранты воркспейса. */
export const useWorkspaceGrants = (workspaceSid: string | null | undefined) =>
  useQuery({
    queryKey: workspaceKeys.grants(workspaceSid ?? '∅'),
    queryFn: () => workspacesClient.listGrants(workspaceSid as string),
    enabled: Boolean(workspaceSid),
  });

/** Выдать действие участнику. */
export const useGrantAction = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GrantActionInput) =>
      workspacesClient.grantAction(workspaceSid, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKeys.grants(workspaceSid),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKeys.myAccess(workspaceSid),
        }),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

/** Отозвать грант. */
export const useRevokeGrant = (workspaceSid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantSid: string) =>
      workspacesClient.revokeGrant(workspaceSid, grantSid),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKeys.grants(workspaceSid),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKeys.myAccess(workspaceSid),
        }),
        invalidateDashboardScope(queryClient, workspaceSid),
      ]),
  });
};

// ── операторская консоль ─────────────────────────────────────────────────────

/** Все воркспейсы (оператор). */
export const useAllWorkspaces = (params?: {enabled?: boolean}) =>
  useQuery({
    queryKey: workspaceKeys.allWorkspaces(),
    queryFn: () => workspacesClient.listAllWorkspaces(),
    enabled: params?.enabled ?? true,
  });

/** Создать воркспейс клиента (оператор). */
export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) =>
      workspacesClient.createWorkspace(input),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: workspaceKeys.allWorkspaces()});
      queryClient.invalidateQueries({queryKey: workspaceKeys.myWorkspaces()});
    },
  });
};

/** Изменить воркспейс. */
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceSid,
      input,
    }: {
      workspaceSid: string;
      input: UpdateWorkspaceInput;
    }) => workspacesClient.updateWorkspace(workspaceSid, input),
    onSuccess: (_data, {workspaceSid}) => {
      queryClient.invalidateQueries({queryKey: workspaceKeys.allWorkspaces()});
      queryClient.invalidateQueries({queryKey: workspaceKeys.myWorkspaces()});
      // Префикс ['workspaces', sid] покрывает полный воркспейс (тема/название) и
      // scoped-данные — чтобы изменения (включая тему) сразу подхватились.
      queryClient.invalidateQueries({queryKey: ['workspaces', workspaceSid]});
    },
  });
};

/** Удалить воркспейс (оператор). */
export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceSid: string) =>
      workspacesClient.deleteWorkspace(workspaceSid),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: workspaceKeys.allWorkspaces()});
      queryClient.invalidateQueries({queryKey: workspaceKeys.myWorkspaces()});
    },
  });
};
